import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, Modal, ConfirmModal, useIsAdmin } from '../components/shared';

const DAY_NAMES = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const MONTH_NAMES = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const POLL_MS = 2000;
const store = { get: k => { try { return localStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} } };
const ymd = d => d.toLocaleDateString('en-CA'); // local YYYY-MM-DD
const parse = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return ymd(d); };
const weekStart = s => { const d = parse(s); return addDays(s, -((d.getDay() + 1) % 7)); }; // week starts Saturday
const pretty = s => { const d = parse(s); return `${DAY_NAMES[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`; };
const hourOf = t => (t ? parseInt(t.slice(0, 2), 10) : null);
const me = () => store.get('pf_username') || '';
const addMonths = (s, n) => { const d = parse(s); return ymd(new Date(d.getFullYear(), d.getMonth() + n, 1)); };
const monthStart = s => s.slice(0, 8) + '01';
const hms = d => d.toLocaleTimeString('en-GB');
const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fromMin = m => `${String(Math.min(23, Math.floor(m / 60))).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const dayDiff = (a, b) => Math.round((parse(b) - parse(a)) / 86400000);
const HOVER_BG = 'rgba(245,158,11,0.18)';
// Drag & drop helpers (desktop HTML5 DnD)
const dragProps = (dnd, t) => !dnd.enabled || t._virtual ? {} : {
  draggable: true,
  onDragStart: e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', t.id); e.dataTransfer.effectAllowed = 'move'; },
};
const dropProps = (dnd, key, target) => !dnd.enabled ? {} : {
  onDragOver: e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dnd.hover !== key) dnd.setHover(key); },
  onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget)) dnd.setHover(null); },
  onDrop: e => { e.preventDefault(); e.stopPropagation(); dnd.setHover(null); const id = e.dataTransfer.getData('text/plain'); if (id) dnd.move(id, target); },
};
const EMPTY = { title: '', start_time: '', end_time: '', assignee: '', client_id: '', notes: '' };

function TaskForm({ task, clients, owners, onSave, onDelete, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...task });
  const [confirm, setConfirm] = useState(false);
  const toast = useToast();
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  async function submit() {
    if (!f.title) return toast('العنوان مطلوب', 'error');
    try { await onSave(f); } catch (e) { toast(e.message, 'error'); }
  }
  return (
    <Modal title="تعديل التاسك" onClose={onClose} footer={
      <>
        <button className="btn btn-danger btn-sm" onClick={() => setConfirm(true)}>حذف</button>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
        <button className="btn btn-primary btn-sm" onClick={submit}>حفظ</button>
      </>
    }>
      <div dir="rtl">
        <div className="field"><label>العنوان</label><input className="input" value={f.title} onChange={set('title')} /></div>
        <div className="grid-2">
          <div className="field"><label>اليوم</label><input className="input" type="date" value={f.date} onChange={set('date')} /></div>
          <div className="field"><label>المسؤول</label>
            <input className="input" list="cal-owners" value={f.assignee || ''} onChange={set('assignee')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>من</label><input className="input" type="time" value={f.start_time || ''} onChange={set('start_time')} /></div>
          <div className="field"><label>إلى</label><input className="input" type="time" value={f.end_time || ''} onChange={set('end_time')} /></div>
        </div>
        <div className="field"><label>العميل</label>
          <select className="select" value={f.client_id || ''} onChange={set('client_id')}>
            <option value="">—</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="field"><label>لحد يوم (لو الشغل أكتر من يوم)</label>
          <input className="input" type="date" min={f.date} value={f.end_date || ''} onChange={set('end_date')} /></div>
        {f.repeat_flag && <div className="text-xs text-muted mb-8">🔁 متكررة من تيكتيك: <span className="mono" dir="ltr">{f.repeat_flag.replace(/^RRULE:/, '')}</span></div>}
        <div className="field"><label>ملاحظات</label><textarea className="input" rows={2} value={f.notes || ''} onChange={set('notes')} /></div>
        <datalist id="cal-owners">{owners.map(o => <option key={o} value={o} />)}</datalist>
      </div>
      {confirm && <ConfirmModal message="حذف التاسك؟" onConfirm={onDelete} onClose={() => setConfirm(false)} />}
    </Modal>
  );
}

function TaskRow({ t, onToggle, onOpen, isAdmin, showDate, dnd }) {
  return (
    <div className="card flex items-center gap-8" {...(dnd ? dragProps(dnd, t) : {})} style={{
      padding: '8px 10px', marginBottom: 6, opacity: t.done ? 0.55 : 1, cursor: dnd && dnd.enabled ? 'grab' : 'default',
      borderInlineStart: `3px solid ${t._pending ? 'var(--yellow)' : 'var(--primary)'}`,
      outline: t._pending ? '1px dashed var(--yellow)' : 'none' }}>
      <input type="checkbox" checked={!!t.done} disabled={!isAdmin || t._virtual} title={t._virtual ? 'علّم الأصلية' : ''} onChange={() => onToggle(t)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
      <div style={{ flex: 1, cursor: isAdmin ? 'pointer' : 'default', minWidth: 0 }} onClick={() => isAdmin && onOpen(t)}>
        <div className="fw-600 text-sm" style={{ textDecoration: t.done ? 'line-through' : 'none' }}>
          {t._pending && <span title="لسه متحفظش" style={{ color: 'var(--yellow)' }}>● </span>}
          {t.repeat && <span title="متكررة">🔁 </span>}
          {t.title}
          {t.span_len > 1 && <span className="text-xs text-dim" style={{ marginInlineStart: 6 }}>↔ يوم {t.span_day}/{t.span_len}</span>}
          {t.ticktick_id && <span title="TickTick" className="text-xs" style={{ marginInlineStart: 6, color: 'var(--primary)' }}>✓TT</span>}
        </div>
        <div className="text-xs text-dim">
          {showDate && <span className="text-red">{pretty(t.date)} · </span>}
          {t.start_time && <span className="mono" dir="ltr">{t.start_time}{t.end_time ? `–${t.end_time}` : ''} · </span>}
          {t.client_name && <span>{t.client_name} · </span>}
          {t.assignee && <span>👤 {t.assignee}</span>}
        </div>
        {t.notes && <div className="text-xs text-muted">{t.notes}</div>}
      </div>
    </div>
  );
}

function EventRow({ icon, color, title, sub, onClick, drag, dnd, pending }) {
  const can = drag && dnd && dnd.enabled;
  return (
    <div className="card" onClick={onClick} {...(can ? dragProps(dnd, drag) : {})} style={{
      padding: '8px 10px', marginBottom: 6, cursor: can ? 'grab' : 'pointer', background: 'var(--bg2)',
      borderInlineStart: `3px solid ${pending ? 'var(--yellow)' : color}`, outline: pending ? '1px dashed var(--yellow)' : 'none' }}>
      <div className="text-sm fw-500">{pending && <span style={{ color: 'var(--yellow)' }}>● </span>}{icon} {title}</div>
      {sub && <div className="text-xs text-dim">{sub}</div>}
    </div>
  );
}

function ScrollMonths({ weeks, day, today, narrow, data, byWho, byOwner, onPick, dnd, scrollRef, onEdge, onScrollMonth, monthLabel, height }) {
  const head = ['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'];
  const SHORT = { 'السبت': 'سبت', 'الأحد': 'أحد', 'الاثنين': 'اثن', 'الثلاثاء': 'ثلا', 'الأربعاء': 'أرب', 'الخميس': 'خمي', 'الجمعة': 'جمع' };
  const max = narrow ? 0 : 3;
  const topRef = useRef(null), bottomRef = useRef(null);

  // Index everything by date once per data change
  const byDate = useMemo(() => {
    const m = {};
    const push = (d, it) => { (m[d] = m[d] || []).push(it); };
    data.tasks.filter(byWho).forEach(t => push(t.date, {
      k: 't' + t.id, task: t, color: t._pending ? 'var(--yellow)' : 'var(--primary)', done: t.done, pending: t._pending,
      text: `${t.repeat ? '🔁 ' : ''}${t.start_time ? t.start_time + ' ' : ''}${t.title}${t.span_len > 1 ? ` (${t.span_day}/${t.span_len})` : ''}` }));
    data.followups.filter(byOwner).forEach(o => push(o.date, { k: 'f' + o.id, drag: { id: 'opp:' + o.id }, pending: o._pending,
      color: o._pending ? 'var(--yellow)' : 'var(--yellow)', text: `🎯 ${o.title}` }));
    (data.parts || []).forEach(x => push(x.date, { k: 'p' + x.id, drag: x.status === 'needed' ? { id: 'part:' + x.id } : null, pending: x._pending,
      color: x._pending ? 'var(--yellow)' : 'var(--red)', done: x.status !== 'needed', text: `🛒 ${x.name}${x.quantity > 1 ? ' ×' + x.quantity : ''}` }));
    (data.pm || []).forEach(m => push(m.date, { k: 'm' + m.id, drag: m.status === 'done' ? null : { id: 'pm:' + m.id }, pending: m._pending,
      done: m.status === 'done' || m.status === 'missed',
      color: m._pending ? 'var(--yellow)' : '#8b5cf6', text: `🛠 ${m.client_name} (${m.logged}/${m.device_count})` }));
    data.received.forEach(t => push(t.date, { k: 'r' + t.id, color: 'var(--text3)', text: `📥 ${t.device_name || ''}` }));
    data.delivered.forEach(t => push(t.date, { k: 'd' + t.id, color: 'var(--green)', text: `📤 ${t.device_name || ''}` }));
    return m;
  }, [data, byWho, byOwner]);

  // Infinite scroll: load more weeks when either end comes into view
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) onEdge(e.target === topRef.current ? 'top' : 'bottom');
    }), { root, rootMargin: '400px 0px' });
    if (topRef.current) io.observe(topRef.current);
    if (bottomRef.current) io.observe(bottomRef.current);
    return () => io.disconnect();
  }, [scrollRef, onEdge, weeks.length]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div ref={scrollRef} onScroll={onScrollMonth} style={{ height, minHeight: 420, overflowY: 'auto', position: 'relative' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', boxShadow: '0 1px 0 var(--border)' }}>
          <div className="fw-600" style={{ padding: '8px 12px', fontSize: 18 }}>{monthLabel}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {head.map(h => <div key={h} className="text-xs text-dim" style={{ padding: '4px', textAlign: 'center' }}>{narrow ? SHORT[h] : h}</div>)}
          </div>
        </div>
        <div ref={topRef} style={{ height: 1 }} />
        {weeks.map(w => (
          <div key={w} data-week={w} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {Array.from({ length: 7 }, (_, i) => addDays(w, i)).map(d => {
              const items = byDate[d] || [];
              const dt = parse(d), first = dt.getDate() === 1;
              const odd = dt.getMonth() % 2 === 1, isT = d === today, sel = d === day;
              const open = items.filter(i => !i.done).length;
              const firstWeek = dt.getDate() <= 7; // top border marks month boundary
              return (
                <div key={d} data-date={d} onClick={() => onPick(d)} {...dropProps(dnd, 'm' + d, { date: d })} style={{
                  minHeight: narrow ? 56 : 104, padding: 4, cursor: 'pointer', overflow: 'hidden',
                  borderInlineEnd: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                  borderTop: firstWeek ? '2px solid var(--primary)' : 'none',
                  borderInlineStart: first ? '2px solid var(--primary)' : 'none',
                  background: dnd.hover === 'm' + d ? HOVER_BG : isT ? 'var(--primary-dim)' : odd ? 'var(--bg2)' : 'var(--bg)',
                  outline: sel ? '2px solid var(--primary)' : 'none', outlineOffset: -2,
                }}>
                  <div className="flex items-center" style={{ justifyContent: 'space-between', gap: 4 }}>
                    <span className={isT || first ? 'fw-600 text-primary' : 'fw-500'} style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {dt.getDate()}{first && !narrow ? ` ${MONTH_NAMES[dt.getMonth()]}` : ''}
                    </span>
                    {narrow && open > 0 && <span className="text-xs" style={{ background: 'var(--primary)', color: '#fff', borderRadius: 8, padding: '0 5px' }}>{open}</span>}
                  </div>
                  {items.slice(0, max).map(i => (
                    <div key={i.k} className="text-xs" title={i.text} {...(i.task || i.drag ? dragProps(dnd, i.task || i.drag) : {})} style={{
                      marginTop: 2, padding: '1px 4px', borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      borderInlineStart: `3px solid ${i.color}`, background: 'var(--bg)', cursor: (i.task || i.drag) && dnd.enabled ? 'grab' : 'pointer',
                      outline: i.pending ? '1px dashed var(--yellow)' : 'none',
                      textDecoration: i.done ? 'line-through' : 'none', opacity: i.done ? 0.6 : 1,
                    }}>{i.text}</div>
                  ))}
                  {!narrow && items.length > max && <div className="text-xs text-dim" style={{ marginTop: 2 }}>+{items.length - max} كمان</div>}
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}

const OPP_STAGE = { new: ['جديدة', 'var(--text3)'], contacted: ['تواصل', 'var(--primary)'], quoted: ['عرض سعر', 'var(--yellow)'] };
const OPP_TYPE = { maintenance: 'صيانة دورية', partnership: 'تعاون', supply: 'توريد', other: 'أخرى' };

function OppPanel({ opps, today, byOwner, dnd, onOpen, height }) {
  const list = opps.filter(o => !['won', 'lost'].includes(o.stage) && byOwner(o)).sort((a, b) => {
    const da = a.next_followup ? a.next_followup.slice(0, 10) : '9999', db = b.next_followup ? b.next_followup.slice(0, 10) : '9999';
    return da < db ? -1 : da > db ? 1 : (b.expected_value || 0) - (a.expected_value || 0);
  });
  const total = list.reduce((s, o) => s + (o.expected_value || 0), 0);
  return (
    <div className="card" style={{ width: 300, flexShrink: 0, padding: 0, position: 'sticky', top: 8, maxHeight: height, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center" style={{ justifyContent: 'space-between' }}>
          <span className="fw-600">🎯 الفرص <span className="text-dim">({list.length})</span></span>
          <a className="link text-xs" href="/opportunities" onClick={e => { e.preventDefault(); onOpen(); }}>فتح الكل ←</a>
        </div>
        <div className="text-xs text-dim">{total ? `${total.toLocaleString('en')} EGP مفتوح` : ''}{dnd.enabled ? ' · اسحب فرصة ليوم لتحديد ميعاد المتابعة' : ''}</div>
      </div>
      <div style={{ overflowY: 'auto', padding: 8 }}>
        {list.length === 0 && <div className="empty text-sm">مفيش فرص مفتوحة</div>}
        {list.map(o => {
          const d = o.next_followup ? o.next_followup.slice(0, 10) : null;
          const late = d && d < today, isT = d === today;
          const [label, color] = OPP_STAGE[o.stage] || [o.stage, 'var(--text3)'];
          return (
            <div key={o.id} className="card" onClick={onOpen}
              {...(dnd.enabled ? { draggable: true, onDragStart: e => { e.dataTransfer.setData('text/plain', 'opp:' + o.id); e.dataTransfer.effectAllowed = 'move'; } } : {})}
              style={{ padding: '8px 10px', marginBottom: 6, cursor: dnd.enabled ? 'grab' : 'pointer',
                borderInlineStart: `3px solid ${o._pending ? 'var(--yellow)' : late ? 'var(--red)' : color}`, outline: o._pending ? '1px dashed var(--yellow)' : 'none' }}>
              <div className="fw-600 text-sm">{o.title}</div>
              <div className="text-xs text-muted">{o.client_name || o.org_name || '—'} · {OPP_TYPE[o.opp_type] || o.opp_type}</div>
              <div className="flex items-center gap-8 mt-4" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <span className="text-xs" style={{ color, fontWeight: 600 }}>● {label}</span>
                {o.expected_value ? <span className="mono text-xs">{Number(o.expected_value).toLocaleString('en')}</span> : null}
              </div>
              <div className={`text-xs mt-4 ${late ? 'text-red fw-600' : isT ? 'text-yellow fw-600' : 'text-dim'}`}>
                ⏰ {d ? `${late ? 'متأخرة · ' : isT ? 'النهارده · ' : ''}${pretty(d)}` : 'من غير ميعاد'}{o.owner ? ` · 👤 ${o.owner}` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ tasks, opps, parts, today, who, isAdmin, onToggle, onOpenTask, navigate }) {
  const [q, setQ] = useState('');
  const [types, setTypes] = useState({ task: true, opp: true, part: true });
  const [showDone, setShowDone] = useState(false);
  const tomorrow = addDays(today, 1), weekEnd = addDays(weekStart(today), 6);
  const match = (...v) => !q || v.some(x => x && String(x).toLowerCase().includes(q.toLowerCase()));

  const rows = [
    ...(types.task ? tasks.filter(t => (!who || t.assignee === who) && (showDone || !t.done) && match(t.title, t.notes, t.client_name, t.assignee))
      .map(t => ({ k: 't' + t.id, kind: 'task', date: t.end_date && t.end_date >= today && t.date < today ? today : t.date, sortTime: t.start_time || '99', t })) : []),
    ...(types.opp ? opps.filter(o => !['won', 'lost'].includes(o.stage) && (!who || o.owner === who) && match(o.title, o.org_name, o.client_name, o.notes))
      .map(o => ({ k: 'o' + o.id, kind: 'opp', date: o.next_followup ? o.next_followup.slice(0, 10) : null, sortTime: '98', o })) : []),
    ...(types.part ? parts.filter(x => (showDone || x.status === 'needed') && x.status !== 'cancelled' && match(x.name, x.device, x.buy_from, x.client_name))
      .map(x => ({ k: 'p' + x.id, kind: 'part', date: x.due_date || null, sortTime: '97', x })) : []),
  ];
  const isDone = r => (r.kind === 'task' ? !!r.t.done : r.kind === 'part' ? r.x.status !== 'needed' : false);
  const groups = [
    ['⚠ متأخر', r => !isDone(r) && r.date && r.date < today, 'var(--red)'],
    ['النهارده', r => !isDone(r) && r.date === today, 'var(--primary)'],
    ['بكره', r => !isDone(r) && r.date === tomorrow, 'var(--text)'],
    ['باقي الأسبوع', r => !isDone(r) && r.date > tomorrow && r.date <= weekEnd, 'var(--text)'],
    ['بعدين', r => !isDone(r) && r.date > weekEnd && r.date > tomorrow, 'var(--text2)'],
    ['من غير ميعاد', r => !isDone(r) && !r.date, 'var(--text3)'],
    ['✓ خلصت', r => isDone(r), 'var(--green)'],
  ];
  const sort = (a, b) => ((a.date || '9999') + a.sortTime < (b.date || '9999') + b.sortTime ? -1 : 1);

  function Row({ r }) {
    const late = !isDone(r) && r.date && r.date < today;
    const dateTxt = r.date ? pretty(r.date) : '';
    if (r.kind === 'task') {
      const t = r.t;
      return (
        <div className="flex items-center gap-8" style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', opacity: t.done ? 0.55 : 1 }}>
          <input type="checkbox" checked={!!t.done} disabled={!isAdmin} onChange={() => onToggle(t)} style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, cursor: isAdmin ? 'pointer' : 'default' }} onClick={() => isAdmin && onOpenTask(t)}>
            <div className="text-sm fw-600" style={{ textDecoration: t.done ? 'line-through' : 'none' }}>
              {t.repeat_flag && '🔁 '}{t.title}
              {t.ticktick_id && <span className="text-xs" style={{ color: 'var(--primary)', marginInlineStart: 6 }}>✓TT</span>}
            </div>
            <div className="text-xs text-dim">
              {[t.client_name, t.assignee && `👤 ${t.assignee}`, t.notes].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className={`text-xs ${late ? 'text-red fw-600' : 'text-muted'}`} style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
            {dateTxt}{t.end_date && t.end_date > t.date ? ` ← ${pretty(t.end_date)}` : ''}
            {t.start_time && <div className="mono" dir="ltr">{t.start_time}{t.end_time ? `–${t.end_time}` : ''}</div>}
          </div>
        </div>
      );
    }
    const isOpp = r.kind === 'opp';
    const title = isOpp ? `🎯 متابعة: ${r.o.title}` : `🛒 شراء: ${r.x.name}${r.x.quantity > 1 ? ' ×' + r.x.quantity : ''}`;
    const sub = isOpp
      ? [r.o.client_name || r.o.org_name, r.o.expected_value && `${Number(r.o.expected_value).toLocaleString('en')} EGP`, r.o.owner && `👤 ${r.o.owner}`]
      : [r.x.client_name, r.x.device, r.x.buy_from];
    return (
      <div className="flex items-center gap-8" onClick={() => navigate(isOpp ? '/opportunities' : '/part-needs')}
        style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', opacity: isDone(r) ? 0.55 : 1 }}>
        <span style={{ width: 18, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-sm fw-500">{title}</div>
          <div className="text-xs text-dim">{sub.filter(Boolean).join(' · ')}</div>
        </div>
        <div className={`text-xs ${late ? 'text-red fw-600' : 'text-muted'}`} style={{ whiteSpace: 'nowrap' }}>{dateTxt}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-8 mb-16 items-center" style={{ flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 240 }} placeholder="🔍 بحث..." value={q} onChange={e => setQ(e.target.value)} />
        {[['task', '📋 تاسكات'], ['opp', '🎯 متابعات'], ['part', '🛒 قطع']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${types[k] ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTypes(x => ({ ...x, [k]: !x[k] }))}>{l}</button>
        ))}
        <button className={`btn btn-sm ${showDone ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setShowDone(v => !v)}>✓ اعرض اللي خلص</button>
        <span className="text-xs text-dim">{rows.filter(r => !isDone(r)).length} مفتوح</span>
      </div>
      {groups.map(([label, fn, color]) => {
        const items = rows.filter(fn).sort(sort);
        if (!items.length) return null;
        return (
          <div key={label} className="card mb-16" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="fw-600 text-sm" style={{ padding: '8px 12px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)', color }}>
              {label} <span className="text-dim">({items.length})</span>
            </div>
            {items.map(r => <Row key={r.k} r={r} />)}
          </div>
        );
      })}
      {rows.length === 0 && <div className="empty">مفيش حاجة</div>}
    </div>
  );
}

export default function Calendar() {
  const [day, setDay] = useState(ymd(new Date()));
  const [data, setData] = useState({ tasks: [], followups: [], received: [], delivered: [], parts: [], pm: [], overdue: { tasks: [], followups: [], parts: [], pm: [] } });
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [quick, setQuick] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [pending, setPending] = useState({}); // taskId -> { date, start_time, end_time } not yet saved
  const [hover, setHover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [who, setWho] = useState('');
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [view, setView] = useState(['month', 'list'].includes(store.get('pf_cal_view')) ? store.get('pf_cal_view') : 'day');
  const [listTasks, setListTasks] = useState([]);
  const [listParts, setListParts] = useState([]);
  const [now, setNow] = useState(new Date());
  const [synced, setSynced] = useState(null);
  const [online, setOnline] = useState(true);
  const [full, setFull] = useState(false);
  const [narrow, setNarrow] = useState(window.innerWidth < 700);
  const [wide, setWide] = useState(window.innerWidth >= 1350);
  const [opps, setOpps] = useState([]);
  const loadOpps = useCallback(() => api.getOpportunities().then(setOpps).catch(() => {}), []);
  useEffect(() => {
    loadOpps();
    const t = setInterval(() => { if (!document.hidden) loadOpps(); }, 5000);
    return () => clearInterval(t);
  }, [loadOpps]);
  const rootRef = useRef(null);
  const lastJson = useRef('');
  const today = ymd(now);
  const ws = weekStart(day);
  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(ws, i)), [ws]);
  // Continuous month scroll: a growing window of weeks
  const [range, setRange] = useState(() => ({ start: addDays(weekStart(ymd(new Date())), -7 * 8), end: addDays(weekStart(ymd(new Date())), 7 * 26) }));
  const weeks = useMemo(() => {
    const out = []; for (let w = range.start; w <= range.end; w = addDays(w, 7)) out.push(w); return out;
  }, [range]);
  const scrollRef = useRef(null);
  const prependRef = useRef(null);   // scroll height before prepending weeks
  const edgeReady = useRef(false);   // block edge-loading until first scroll-to-date
  const [scrollTo, setScrollTo] = useState(view === 'month' ? day : null);
  const [monthLabel, setMonthLabel] = useState('');
  const from = view === 'month' ? range.start : ws;
  const to = view === 'month' ? addDays(range.end, 6) : addDays(ws, 6);

  // Live sync: poll every 2s while the tab is visible; only re-render when data actually changed
  const load = useCallback((silent) => {
    return api.getCalendar(from, to).then(d => {
      const j = JSON.stringify(d);
      if (j !== lastJson.current) { lastJson.current = j; setData(d); }
      setSynced(new Date()); setOnline(true);
    }).catch(e => { setOnline(false); if (!silent) toast(e.message, 'error'); });
  }, [from, to, toast]);
  useEffect(() => {
    lastJson.current = '';
    load();
    const poll = setInterval(() => { if (!document.hidden) load(true); }, POLL_MS);
    const onVis = () => { if (!document.hidden) load(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onFs = () => setFull(document.fullscreenElement === rootRef.current);
    const onResize = () => { setNarrow(window.innerWidth < 700); setWide(window.innerWidth >= 1350); };
    document.addEventListener('fullscreenchange', onFs);
    window.addEventListener('resize', onResize);
    return () => { document.removeEventListener('fullscreenchange', onFs); window.removeEventListener('resize', onResize); };
  }, []);
  useEffect(() => { store.set('pf_cal_view', view); }, [view]);

  function toggleFull() {
    const el = rootRef.current;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else if (el && el.requestFullscreen) el.requestFullscreen().catch(() => setFull(f => !f));
    else setFull(f => !f); // fallback (iOS Safari): CSS overlay
  }
  function goTo(d) {
    setDay(d);
    if (view !== 'month') return;
    const w = weekStart(d);
    if (w < range.start || w > range.end) setRange({ start: addDays(w, -7 * 8), end: addDays(w, 7 * 26) });
    setScrollTo(d);
  }
  const step = n => (view === 'month' ? goTo(addMonths(day, n)) : setDay(addDays(day, n)));

  const onEdge = useCallback(dir => {
    if (!edgeReady.current) return;
    if (dir === 'bottom') setRange(r => ({ ...r, end: addDays(r.end, 7 * 12) }));
    else {
      const el = scrollRef.current;
      prependRef.current = el ? el.scrollHeight - el.scrollTop : null;
      setRange(r => ({ ...r, start: addDays(r.start, -7 * 12) }));
    }
  }, []);
  // keep the view steady after prepending weeks above
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && prependRef.current != null) { el.scrollTop = el.scrollHeight - prependRef.current; prependRef.current = null; }
  }, [range.start]);
  // jump to a date's week
  useLayoutEffect(() => {
    if (view !== 'month' || !scrollTo) return;
    const el = scrollRef.current;
    const row = el && el.querySelector(`[data-week="${weekStart(scrollTo)}"]`);
    if (row) {
      const header = el.firstChild ? el.firstChild.offsetHeight : 0;
      el.scrollTop = row.offsetTop - header;
      setScrollTo(null);
      setTimeout(() => { edgeReady.current = true; }, 300);
    }
  }, [scrollTo, view, weeks]);
  useEffect(() => { if (view === 'month') { edgeReady.current = false; setScrollTo(day); } }, [view]); // eslint-disable-line
  const onScrollMonth = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const header = el.firstChild ? el.firstChild.offsetHeight : 0;
    const rows = el.querySelectorAll('[data-week]');
    for (const r of rows) {
      if (r.offsetTop + r.offsetHeight > el.scrollTop + header + 10) {
        const mid = parse(addDays(r.dataset.week, 3));
        const label = `${MONTH_NAMES[mid.getMonth()]} ${mid.getFullYear()}`;
        setMonthLabel(l => (l === label ? l : label));
        break;
      }
    }
  }, []);
  useEffect(() => { if (view === 'month') onScrollMonth(); }, [view, weeks, onScrollMonth]);
  const openDay = d => { setDay(d); setView('day'); };
  useEffect(() => {
    api.getClients().then(setClients).catch(() => {});
    api.getUsers().then(u => setUsers(u.map(x => x.username))).catch(() => {});
  }, []);

  const owners = useMemo(() => [...new Set([...users, ...data.tasks.map(t => t.assignee).filter(Boolean)])], [users, data.tasks]);
  const byWho = useCallback(t => !who || t.assignee === who, [who]);
  const byOwner = useCallback(o => !who || o.owner === who, [who]);

  // All known tasks (this range + overdue) with unsaved moves applied on top
  const allTasks = useMemo(() => {
    const m = new Map();
    [...data.overdue.tasks, ...data.tasks].forEach(t => m.set(t.id, t));
    return [...m.values()].map(t => pending[t.id] ? { ...t, ...pending[t.id], _pending: true } : t);
  }, [data, pending]);
  const pendingCount = Object.keys(pending).length;
  // Follow-ups come from the opportunities list (covers any date); parts from range + overdue
  const followupsAll = useMemo(() => opps
    .filter(o => !['won', 'lost'].includes(o.stage) && (o.next_followup || pending['opp:' + o.id]))
    .map(o => {
      const pd = pending['opp:' + o.id];
      return { id: o.id, title: o.title, owner: o.owner, stage: o.stage, client_name: o.client_name || o.org_name,
        date: pd ? pd.date : o.next_followup.slice(0, 10), _pending: !!pd };
    }), [opps, pending]);
  const partsAll = useMemo(() => {
    const m = new Map();
    [...((data.overdue && data.overdue.parts) || []).map(x => ({ status: 'needed', ...x })), ...(data.parts || [])].forEach(x => m.set(x.id, x));
    return [...m.values()].map(x => pending['part:' + x.id] ? { ...x, date: pending['part:' + x.id].date, _pending: true } : x);
  }, [data, pending]);
  const pmAll = useMemo(() => {
    const out = [];
    const seen = new Set();
    [...((data.overdue && data.overdue.pm) || []), ...(data.pm || [])].forEach(m => {
      if (seen.has(m.id)) return; seen.add(m.id);
      const pd = pending['pm:' + m.id];
      out.push(pd ? { ...m, date: pd.date, _pending: true } : m);
    });
    return out;
  }, [data, pending]);
  const oppsView = useMemo(() => opps.map(o => pending['opp:' + o.id] ? { ...o, next_followup: pending['opp:' + o.id].date, _pending: true } : o), [opps, pending]);
  const monthData = useMemo(() => ({ ...data, tasks: allTasks, followups: followupsAll, parts: partsAll, pm: pmAll }), [data, allTasks, followupsAll, partsAll, pmAll]);

  function move(id, target) {
    if (id.startsWith('pm:')) {
      const rid = id.slice(3);
      const origDate = ((data.pm || []).concat((data.overdue && data.overdue.pm) || []).find(m => m.id === rid) || {}).date;
      setPending(p => { const n = { ...p }; if (target.date === origDate) delete n[id]; else n[id] = { date: target.date }; return n; });
      return;
    }
    if (id.startsWith('opp:') || id.startsWith('part:')) {
      const [kind, rid] = [id.split(':')[0], id.slice(id.indexOf(':') + 1)];
      const origDate = kind === 'opp'
        ? ((opps.find(o => o.id === rid) || {}).next_followup || '').slice(0, 10)
        : (partsAll.find(x => x.id === rid) || {}).date;
      setPending(p => { const n = { ...p }; if (target.date === origDate) delete n[id]; else n[id] = { date: target.date }; return n; });
      return;
    }
    const found = [...data.overdue.tasks, ...data.tasks].find(t => t.id === id);
    if (!found || found._virtual) return;
    const orig = { ...found, start_time: found.base_start_time !== undefined ? found.base_start_time : found.start_time,
      end_time: found.base_end_time !== undefined ? found.base_end_time : found.end_time };
    const cur = { ...orig, ...(pending[id] || {}) };
    const next = { date: target.date, start_time: cur.start_time || null, end_time: cur.end_time || null };
    if (orig.end_date) next.end_date = parse(orig.end_date) ? addDays(orig.end_date, dayDiff(orig.date, target.date)) : null;
    if (target.untimed) { next.start_time = null; next.end_time = null; }
    if (target.hour !== undefined) {
      const dur = cur.start_time && cur.end_time ? Math.max(0, toMin(cur.end_time) - toMin(cur.start_time)) : 0;
      const start = target.hour * 60 + (cur.start_time ? toMin(cur.start_time) % 60 : 0);
      next.start_time = fromMin(start);
      next.end_time = dur ? fromMin(Math.min(start + dur, 23 * 60 + 59)) : null;
    }
    const same = next.date === orig.date && (next.start_time || null) === (orig.start_time || null) && (next.end_time || null) === (orig.end_time || null);
    setPending(p => { const n = { ...p }; if (same) delete n[id]; else n[id] = next; return n; });
  }
  const dnd = { enabled: isAdmin, hover, setHover, move };

  async function saveMoves() {
    setSaving(true);
    let ok = 0; const failed = {};
    for (const [id, patch] of Object.entries(pending)) {
      try {
        if (id.startsWith('opp:')) await api.updateOpportunity(id.slice(4), { next_followup: patch.date, author: me() });
        else if (id.startsWith('part:')) await api.updatePartNeed(id.slice(5), { due_date: patch.date });
        else if (id.startsWith('pm:')) await api.pmUpdateVisit(id.slice(3), { scheduled_date: patch.date });
        else await api.updateTask(id, patch);
        ok++;
      } catch (e) { failed[id] = patch; }
    }
    try { const st = await api.ticktickStatus(); if (st.connected && st.project_id) await api.ticktickSync(); } catch (e) {}
    setPending(failed);
    setSaving(false);
    await Promise.all([load(), loadOpps()]);
    if (Object.keys(failed).length) toast(`اتحفظ ${ok}، وفشل ${Object.keys(failed).length}`, 'error');
    else toast(`اتحفظ واتزامن ${ok} ✓`);
  }

  useEffect(() => {
    if (!pendingCount) return;
    const h = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [pendingCount]);

  const dayTasks = allTasks.filter(t => t.date === day && byWho(t));
  const untimed = dayTasks.filter(t => !t.start_time);
  const timed = dayTasks.filter(t => t.start_time);
  const followups = followupsAll.filter(o => o.date === day && byOwner(o));
  const parts = partsAll.filter(x => x.date === day);
  const pmDay = pmAll.filter(m => m.date === day);
  const overduePm = day === today ? pmAll.filter(m => (m.status === 'planned' || m.status === 'partial') && m.date < today) : [];
  const overdueParts = day === today ? partsAll.filter(x => x.status === 'needed' && x.date < today) : [];
  const received = data.received.filter(t => t.date === day);
  const delivered = data.delivered.filter(t => t.date === day);
  const overdueTasks = day === today ? allTasks.filter(t => !t.done && !t._virtual && t.date < today && (!t.end_date || t.end_date < today) && byWho(t)) : [];
  const openEdit = t => setEdit(t.series_id
    ? { ...t, id: t.series_id, date: t.base_date, start_time: t.base_start_time, end_time: t.base_end_time }
    : t);
  const overdueFollowups = day === today ? followupsAll.filter(o => o.date < today && byOwner(o)) : [];

  const hours = useMemo(() => {
    const hs = timed.map(t => hourOf(t.start_time));
    const lo = Math.min(8, ...hs), hi = Math.max(21, ...hs);
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  }, [timed]);
  const nowHour = now.getHours();
  const nowMin = now.getMinutes();

  const countFor = d => pmAll.filter(m => m.date === d && m.status !== 'done' && m.status !== 'missed').length + partsAll.filter(x => x.date === d && x.status === 'needed').length + allTasks.filter(t => t.date === d && !t.done && byWho(t)).length + followupsAll.filter(o => o.date === d && byOwner(o)).length;

  async function quickAdd(e) {
    e.preventDefault();
    if (!quick.title.trim()) return;
    try {
      await api.createTask({ ...quick, title: quick.title.trim(), date: day, assignee: quick.assignee || who || me() });
      setQuick(q => ({ ...EMPTY, assignee: q.assignee }));
      load();
    } catch (err) { toast(err.message, 'error'); }
  }

  const loadList = useCallback(() => Promise.all([
    api.getTasks().then(setListTasks), api.getPartNeeds().then(setListParts),
  ]).catch(() => {}), []);
  useEffect(() => {
    if (view !== 'list') return;
    loadList();
    const t = setInterval(() => { if (!document.hidden) loadList(); }, 3000);
    return () => clearInterval(t);
  }, [view, loadList]);

  async function toggle(t) {
    setListTasks(l => l.map(x => x.id === t.id ? { ...x, done: t.done ? 0 : 1 } : x));
    setData(d => ({ ...d, tasks: d.tasks.map(x => x.id === t.id ? { ...x, done: t.done ? 0 : 1 } : x),
      overdue: { ...d.overdue, tasks: d.overdue.tasks.map(x => x.id === t.id ? { ...x, done: t.done ? 0 : 1 } : x) } }));
    try { await api.updateTask(t.id, { done: !t.done }); load(); if (view === 'list') loadList(); } catch (e) { toast(e.message, 'error'); load(); }
  }

  async function save(f) {
    if (f.end_date && f.end_date < f.date) throw new Error('تاريخ النهاية قبل البداية');
    await api.updateTask(f.id, { title: f.title, date: f.date, start_time: f.start_time, end_time: f.end_time, end_date: f.end_date && f.end_date > f.date ? f.end_date : '', assignee: f.assignee, client_id: f.client_id, notes: f.notes });
    toast('اتحفظ'); setEdit(null); load(); if (view === 'list') loadList();
  }
  async function remove() { await api.deleteTask(edit.id); setEdit(null); load(); if (view === 'list') loadList(); }
  async function moveToToday(t) { await api.updateTask(t.id, { date: today }); load(); }

  const setQ = k => e => setQuick(q => ({ ...q, [k]: e.target.value }));
  const doneCount = dayTasks.filter(t => t.done).length;

  return (
    <div ref={rootRef} className="page" dir="rtl" style={full ? { position: 'fixed', inset: 0, zIndex: 900, background: 'var(--bg)', overflow: 'auto', maxWidth: 'none', padding: 20 } : wide ? { maxWidth: 'none' } : {}}>
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-12" style={{ flexWrap: 'wrap' }}>
          <h1 className="page-title" dir="ltr">CALENDAR <span>_</span></h1>
          <span className="text-xs mono" title={synced ? `آخر تحديث ${hms(synced)}` : ''} style={{ color: online ? 'var(--green)' : 'var(--red)' }}>
            ● {online ? 'مباشر' : 'غير متصل'} · {hms(now)}
          </span>
        </div>
        <div className="flex gap-8 items-center" style={{ flexWrap: 'wrap' }}>
          <div className="flex" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <button className={`btn btn-sm ${view === 'day' ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 0, border: 'none' }} onClick={() => setView('day')}>يومي</button>
            <button className={`btn btn-sm ${view === 'month' ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 0, border: 'none' }} onClick={() => setView('month')}>شهري</button>
            <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 0, border: 'none' }} onClick={() => setView('list')}>قائمة</button>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => step(-1)}>→</button>
          <button className={`btn btn-sm ${day === today ? 'btn-primary' : 'btn-ghost'}`} onClick={() => goTo(today)}>النهارده</button>
          <button className="btn btn-ghost btn-sm" onClick={() => step(1)}>←</button>
          <input className="input" type="date" style={{ width: 'auto' }} value={day} onChange={e => e.target.value && goTo(e.target.value)} />
          <select className="select" style={{ width: 'auto' }} value={who} onChange={e => setWho(e.target.value)}>
            <option value="">الكل</option>
            {owners.map(o => <option key={o} value={o}>👤 {o}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={toggleFull} title="ملء الشاشة">{full ? '🗗 خروج' : '⛶ شاشة كاملة'}</button>
        </div>
      </div>

      <div style={wide ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : {}}>
      {wide && <OppPanel opps={oppsView} today={today} byOwner={byOwner} dnd={dnd} onOpen={() => navigate('/opportunities')}
        height={full ? 'calc(100vh - 110px)' : 'calc(100vh - 140px)'} />}
      <div style={wide ? { flex: 1, minWidth: 0 } : {}}>
      {pendingCount > 0 && (
        <div className="card flex items-center gap-8" style={{ position: 'sticky', top: 8, zIndex: 20, padding: '10px 12px', marginBottom: 12, flexWrap: 'wrap',
          background: 'var(--bg)', border: '1px solid var(--yellow)', boxShadow: 'var(--shadow-md)' }}>
          <span className="fw-600 text-sm" style={{ color: 'var(--yellow)' }}>● {pendingCount} تغيير لسه متحفظش</span>
          <div className="flex gap-8" style={{ marginInlineStart: 'auto' }}>
            <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setPending({})}>إلغاء</button>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={saveMoves}>{saving ? '...جاري الحفظ' : '⟳ حفظ ومزامنة'}</button>
          </div>
        </div>
      )}
      {isAdmin && !narrow && pendingCount === 0 && view !== 'list' && <div className="text-xs text-dim mb-8">💡 اسحب أي تاسك أو فرصة أو قطعة غيار لأي يوم، وبعدين اضغط حفظ</div>}

      {view === 'month' && (
        <ScrollMonths weeks={weeks} day={day} today={today} narrow={narrow} data={monthData}
          byWho={byWho} byOwner={byOwner} onPick={openDay} dnd={dnd} scrollRef={scrollRef} onEdge={onEdge}
          onScrollMonth={onScrollMonth} monthLabel={monthLabel} height={full ? 'calc(100vh - 150px)' : 'calc(100vh - 230px)'} />
      )}

      {view === 'list' && (
        <ListView tasks={listTasks} opps={opps} parts={listParts} today={today} who={who} isAdmin={isAdmin}
          onToggle={toggle} onOpenTask={openEdit} navigate={navigate} />
      )}

      {view === 'day' && <>
      {/* Week strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 16 }}>
        {week.map(d => {
          const n = countFor(d), sel = d === day, isT = d === today;
          return (
            <button key={d} onClick={() => setDay(d)} {...dropProps(dnd, 'w' + d, { date: d })} className="card" style={{
              padding: '8px 4px', cursor: 'pointer', textAlign: 'center',
              background: hover === 'w' + d ? HOVER_BG : sel ? 'var(--primary)' : isT ? 'var(--primary-dim)' : 'var(--bg)',
              color: sel ? '#fff' : 'var(--text)', border: `1px solid ${sel ? 'var(--primary)' : 'var(--border)'}`,
            }}>
              <div className="text-xs" style={{ opacity: 0.8 }}>{DAY_NAMES[parse(d).getDay()]}</div>
              <div className="fw-600" style={{ fontSize: 18 }}>{parse(d).getDate()}</div>
              <div className="text-xs" style={{ minHeight: 14 }}>{n ? `● ${n}` : ''}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-8 mb-16" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div className="fw-600" style={{ fontSize: 18 }}>{pretty(day)}{day === today && <span className="text-primary text-sm"> · النهارده</span>}</div>
        <div className="text-sm text-muted">تاسكات: <b>{doneCount}/{dayTasks.length}</b> · متابعات: <b>{followups.length}</b> · قطع: <b>{parts.length}</b> · صيانة: <b>{pmDay.length}</b> · استلام: <b>{received.length}</b> · تسليم: <b>{delivered.length}</b></div>
      </div>

      {isAdmin && (
        <form onSubmit={quickAdd} className="card mb-16" style={{ padding: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
            <input className="input" style={{ gridColumn: 'span 2' }} placeholder={`تاسك جديد ليوم ${pretty(day)}...`} value={quick.title} onChange={setQ('title')} />
            <input className="input" type="time" title="من" value={quick.start_time} onChange={setQ('start_time')} />
            <input className="input" type="time" title="إلى" value={quick.end_time} onChange={setQ('end_time')} />
            <input className="input" list="cal-owners-q" placeholder="المسؤول" value={quick.assignee} onChange={setQ('assignee')} />
            <select className="select" value={quick.client_id} onChange={setQ('client_id')}>
              <option value="">العميل —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="btn btn-primary" type="submit">+ إضافة</button>
          </div>
          <datalist id="cal-owners-q">{owners.map(o => <option key={o} value={o} />)}</datalist>
        </form>
      )}

      {(overdueTasks.length > 0 || overdueFollowups.length > 0 || overdueParts.length > 0 || overduePm.length > 0) && (
        <div className="card mb-16" style={{ padding: 10, borderInlineStart: '3px solid var(--red)' }}>
          <div className="fw-600 text-sm text-red mb-8">⚠ متأخر ({overdueTasks.length + overdueFollowups.length + overdueParts.length + overduePm.length})</div>
          {overduePm.map(m => (
            <EventRow key={'opm' + m.id} drag={{ id: 'pm:' + m.id }} dnd={dnd} pending={m._pending} icon="🛠" color="var(--red)"
              title={`زيارة صيانة: ${m.client_name}`} sub={`${pretty(m.date)} · زيارة ${m.seq}/${m.visits_total} · ${m.logged}/${m.device_count} جهاز`}
              onClick={() => navigate(`/maintenance/visit/${m.id}`)} />
          ))}
          {overdueParts.map(x => (
            <EventRow key={'op' + x.id} drag={{ id: 'part:' + x.id }} dnd={dnd} pending={x._pending} icon="🛒" color="var(--red)" title={`شراء: ${x.name}${x.quantity > 1 ? ' ×' + x.quantity : ''}`}
              sub={`${pretty(x.date)}${x.client_name ? ` · ${x.client_name}` : ''}${x.buy_from ? ` · ${x.buy_from}` : ''}`} onClick={() => navigate('/part-needs')} />
          ))}
          {overdueTasks.map(t => (
            <div key={t.id} className="flex items-center gap-8">
              <div style={{ flex: 1 }}><TaskRow t={t} onToggle={toggle} onOpen={openEdit} isAdmin={isAdmin} showDate dnd={dnd} /></div>
              {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => moveToToday(t)}>للنهارده</button>}
            </div>
          ))}
          {overdueFollowups.map(o => (
            <EventRow key={o.id} drag={{ id: 'opp:' + o.id }} dnd={dnd} pending={o._pending} icon="🎯" color="var(--red)" title={`متابعة: ${o.title}`}
              sub={`${pretty(o.date)}${o.client_name ? ` · ${o.client_name}` : ''}${o.owner ? ` · 👤 ${o.owner}` : ''}`}
              onClick={() => navigate('/opportunities')} />
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, alignItems: 'start' }}>
        {/* All-day column */}
        <div {...dropProps(dnd, 'untimed', { date: day, untimed: true })} style={{ minHeight: 120, borderRadius: 'var(--radius)', background: hover === 'untimed' ? HOVER_BG : 'transparent' }}>
          <div className="section-title mb-8">بدون وقت</div>
          {untimed.length === 0 && followups.length === 0 && parts.length === 0 && pmDay.length === 0 && received.length === 0 && delivered.length === 0 &&
            <div className="empty text-sm">مفيش حاجة</div>}
          {untimed.map(t => <TaskRow key={t.id} t={t} onToggle={toggle} onOpen={openEdit} isAdmin={isAdmin} dnd={dnd} />)}
          {followups.map(o => (
            <EventRow key={o.id} drag={{ id: 'opp:' + o.id }} dnd={dnd} pending={o._pending} icon="🎯" color="var(--yellow)" title={`متابعة: ${o.title}`}
              sub={`${o.client_name || ''}${o.owner ? ` · 👤 ${o.owner}` : ''}`} onClick={() => navigate('/opportunities')} />
          ))}
          {pmDay.map(m => (
            <EventRow key={'pm' + m.id} drag={m.status === 'done' ? null : { id: 'pm:' + m.id }} dnd={dnd} pending={m._pending}
              icon={m.status === 'done' ? '✅' : '🛠'} color={m.status === 'done' ? 'var(--green)' : '#8b5cf6'}
              title={`زيارة صيانة: ${m.client_name}`}
              sub={[`زيارة ${m.seq}/${m.visits_total}`, `${m.logged}/${m.device_count} جهاز`, m.assignee && `👤 ${m.assignee}`].filter(Boolean).join(' · ')}
              onClick={() => navigate(`/maintenance/visit/${m.id}`)} />
          ))}
          {parts.map(x => (
            <EventRow key={'p' + x.id} drag={x.status === 'needed' ? { id: 'part:' + x.id } : null} dnd={dnd} pending={x._pending} icon={x.status === 'needed' ? '🛒' : '✅'} color={x.status === 'needed' ? 'var(--red)' : 'var(--green)'}
              title={`شراء: ${x.name}${x.quantity > 1 ? ' ×' + x.quantity : ''}`}
              sub={`${x.client_name || ''}${x.buy_from ? ` · ${x.buy_from}` : ''}`} onClick={() => navigate('/part-needs')} />
          ))}
          {received.map(t => (
            <EventRow key={'r' + t.id} icon="📥" color="var(--primary)" title={`استلام: ${t.device_name || ''} ${t.serial ? `(${t.serial})` : ''}`}
              sub={`${t.client_name || ''}${t.fault_description ? ` · ${t.fault_description}` : ''}`} onClick={() => navigate(`/tickets/${t.id}`)} />
          ))}
          {delivered.map(t => (
            <EventRow key={'d' + t.id} icon="📤" color="var(--green)" title={`تسليم: ${t.device_name || ''} ${t.serial ? `(${t.serial})` : ''}`}
              sub={t.client_name || ''} onClick={() => navigate(`/tickets/${t.id}`)} />
          ))}
        </div>
        {/* Timeline */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {hours.map(h => {
            const items = timed.filter(t => hourOf(t.start_time) === h);
            const isNow = day === today && h === nowHour;
            return (
              <div key={h} {...dropProps(dnd, 'h' + h, { date: day, hour: h })} style={{ display: 'flex', borderBottom: '1px solid var(--border)', minHeight: 44, background: hover === 'h' + h ? HOVER_BG : isNow ? 'var(--primary-dim)' : 'transparent' }}>
                <div className="mono text-xs" style={{ width: 52, padding: '6px 8px', color: isNow ? 'var(--primary)' : 'var(--text3)', borderInlineEnd: '1px solid var(--border)', flexShrink: 0 }}>
                  {String(h).padStart(2, '0')}:00
                </div>
                <div style={{ flex: 1, padding: items.length ? '4px 6px 0' : 0, position: 'relative' }}>
                  {isNow && <div style={{ position: 'absolute', insetInline: 0, top: `${(nowMin / 60) * 100}%`, height: 2, background: 'var(--red)', zIndex: 1 }} />}
                  {items.map(t => <TaskRow key={t.id} t={t} onToggle={toggle} onOpen={openEdit} isAdmin={isAdmin} dnd={dnd} />)}
                </div>
              </div>
            );
          })}
        </div>

      </div>
      </>}
      </div>
      </div>

      {edit && <TaskForm task={edit} clients={clients} owners={owners} onSave={save} onDelete={remove} onClose={() => setEdit(null)} />}
    </div>
  );
}
